import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Menu,
  Stack,
  Typography,
} from '@mui/material';
import {
  MenuBookOutlined as MenuBookOutlinedIcon,
  ScienceOutlined as ScienceOutlinedIcon,
} from '@mui/icons-material';
import { useLearningLab } from '../../contexts/LearningLabContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { getLabMissionTourSlugs } from '../../../shared/onboarding/tourCatalog';
import { getMissionProgressDisplay } from '../../../shared/onboarding/buildMissionTourSteps';
import { getLearningLabPlaybook } from '../../../shared/onboarding/learningLabPlaybooks';
import { findResumableLabMission } from '../../../shared/onboarding/missionSessionStorage';
import { LearningLabMenuSection } from '../learning-lab/LearningLabMenuSection';
import { useLearningLabNav } from '../learning-lab/useLearningLabNav';
import { LEARNING_LAB_BANNER_HEIGHT_CSS_VAR } from '../../constants/learningLabLayout';
import { PWA_SAFE_AREA_TOP } from '../../constants/pwaSafeAreaLayout';
import { coerceTourPlainText } from '../../../shared/utils/coerceEventListingText';

const bannerButtonSx = {
  borderColor: '#c2410c',
  color: '#7c2d12',
  '&:hover': { borderColor: '#9a3412', bgcolor: 'rgba(194,65,12,0.08)' },
} as const;

export const LearningLabBanner: React.FC = () => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const { active, isStale, exit, refreshLabData, exiting } = useLearningLab();
  const { state, startTour, isFlowCompleted } = useOnboarding();
  const { user } = useAuth();
  const { navState } = useLearningLabNav();
  const [toursMenuAnchor, setToursMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const toursMenuOpen = Boolean(toursMenuAnchor);

  useEffect(() => {
    if (!toursMenuOpen) return;
    const handleScrollClose = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"]')
      ) {
        return;
      }
      setToursMenuAnchor(null);
    };
    window.addEventListener('scroll', handleScrollClose, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScrollClose, true);
    };
  }, [toursMenuOpen]);

  const missionPlaybook = useMemo(() => {
    if (!state.missionMode || !state.currentFlowId) {
      return null;
    }
    return getLearningLabPlaybook(state.currentFlowId) ?? null;
  }, [state.currentFlowId, state.missionMode]);

  const missionStepLabel = useMemo(() => {
    if (!state.missionMode || !state.isRunning || !missionPlaybook) {
      return null;
    }
    const progress = getMissionProgressDisplay(
      state.currentSteps,
      state.currentStepIndex,
      missionPlaybook,
    );
    if (!progress) {
      return null;
    }
    return `${coerceTourPlainText(progress.current, '0')}/${coerceTourPlainText(progress.total, '0')}`;
  }, [
    missionPlaybook,
    state.currentStepIndex,
    state.currentSteps.length,
    state.isRunning,
    state.missionMode,
  ]);

  const labMissionProgress = useMemo(() => {
    const slugs = getLabMissionTourSlugs();
    const completed = slugs.filter((id) => isFlowCompleted(id)).length;
    return { completed, total: slugs.length };
  }, [isFlowCompleted]);

  const savedMissionSlug = useMemo(() => {
    if (!user?.id || state.isRunning) {
      return null;
    }
    return findResumableLabMission(user.id, getLabMissionTourSlugs());
  }, [state.isRunning, user?.id]);

  const defaultResume = useCallback(() => {
    const slug =
      state.currentFlowId ??
      savedMissionSlug ??
      findResumableLabMission(user?.id ?? 0, getLabMissionTourSlugs());
    if (slug) {
      void startTour(slug);
    }
  }, [savedMissionSlug, startTour, state.currentFlowId, user?.id]);

  const canAutoResume = active && Boolean(savedMissionSlug);
  const showResume =
    !state.isRunning &&
    !canAutoResume &&
    Boolean(savedMissionSlug || (state.missionMode && state.currentFlowId));

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!active) {
      root.style.setProperty(LEARNING_LAB_BANNER_HEIGHT_CSS_VAR, '0px');
      return;
    }
    const node = bannerRef.current;
    if (!node) {
      return;
    }
    const syncHeight = () => {
      root.style.setProperty(
        LEARNING_LAB_BANNER_HEIGHT_CSS_VAR,
        `${node.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.setProperty(LEARNING_LAB_BANNER_HEIGHT_CSS_VAR, '0px');
    };
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <Box
      ref={bannerRef}
      sx={{
        position: 'sticky',
        top: 0,
        pt: PWA_SAFE_AREA_TOP,
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      data-tour-stable="learning-lab-banner"
    >
    <>
      <Alert
        severity="warning"
        icon={<ScienceOutlinedIcon fontSize="inherit" />}
        sx={{
          borderRadius: 0,
          bgcolor: '#ffedd5',
          color: '#7c2d12',
          borderTop: '2px solid #c2410c',
          borderBottom: '2px solid #c2410c',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
          '& .MuiAlert-icon': {
            color: '#c2410c',
          },
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          width="100%"
        >
          <Box>
            <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, color: '#7c2d12' }}>
              Practice in the APT Learning Lab
              {missionPlaybook != null && missionStepLabel != null
                ? ` — ${coerceTourPlainText(missionPlaybook.title, 'Practice mission')} (${missionStepLabel})`
                : null}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 500 }}>
              Sample agency data only — your real agency is never changed.
              {isStale
                ? ' Lab content was updated — refresh your session to see the latest sample data.'
                : null}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              label={`Practice missions: ${labMissionProgress.completed}/${labMissionProgress.total}`}
              data-testid="learning-lab-mission-progress-chip"
              sx={{
                borderColor: '#c2410c',
                color: '#7c2d12',
                fontWeight: 600,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<MenuBookOutlinedIcon />}
              aria-expanded={toursMenuOpen}
              aria-haspopup="menu"
              data-testid="learning-lab-banner-tours-toggle"
              onClick={(event) => {
                if (toursMenuOpen) {
                  setToursMenuAnchor(null);
                  return;
                }
                setToursMenuAnchor(event.currentTarget);
              }}
              sx={{
                ...bannerButtonSx,
                ...(toursMenuOpen
                  ? { bgcolor: 'rgba(194,65,12,0.12)', borderColor: '#9a3412' }
                  : {}),
              }}
            >
              {toursMenuOpen ? 'Close tours' : 'Lab tours'}
            </Button>
            {isStale ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => void refreshLabData()}
                sx={bannerButtonSx}
              >
                Refresh lab data
              </Button>
            ) : null}
            {showResume ? (
              <Button
                size="small"
                variant="outlined"
                onClick={defaultResume}
                sx={bannerButtonSx}
              >
                Resume tour
              </Button>
            ) : null}
            <Button
              size="small"
              variant="contained"
              disabled={exiting}
              onClick={() => void exit()}
              data-testid="learning-lab-banner-exit"
              sx={{
                bgcolor: '#c2410c',
                color: '#fff',
                fontWeight: 600,
                '&:hover': { bgcolor: '#9a3412' },
              }}
            >
              {exiting ? 'Exiting…' : 'Exit Lab'}
            </Button>
          </Stack>
        </Stack>
      </Alert>
      <Menu
        anchorEl={toursMenuAnchor}
        open={toursMenuOpen}
        onClose={() => setToursMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Keep sticky Learning Lab banner + AppBar pinned while tours menu is open.
        disableScrollLock
        PaperProps={{
          sx: { minWidth: 280, maxWidth: 360, mt: 0.5 },
        }}
        data-testid="learning-lab-banner-tours-menu"
      >
        <LearningLabMenuSection
          variant="menu"
          navState={navState}
          toursOnly
          onActionComplete={() => setToursMenuAnchor(null)}
        />
      </Menu>
    </>
    </Box>
  );
};

export default LearningLabBanner;
